/* ============================================
   Portal Hosting — Client hosting overview
   Shows hosting plans in all states with DNS guide,
   expiry countdown, and invoice request
   ============================================ */

import { useState } from 'react'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useCachedData } from '@/hooks/useCachedData'
import { useApi } from '@/hooks/useApi'
import { formatDate } from '@/lib/utils'
import { showToast } from '@/components/layout/Toast'
import DnsInstructions from '@/components/hosting/DnsInstructions'
import { Breadcrumbs } from '@/components/ui'

interface HostingDominio {
  id: number
  pedido_id: number
  dominio: string
  tipo: string
  estado: string
  dns_configurado: boolean
}

interface HostingPlan {
  id: number
  codigo: string
  plan_nombre: string
  periodo: string
  estado: string
  estado_provisioning: string
  dominio: string | null
  cpanel_url: string | null
  cpanel_usuario: string | null
  ip_servidor: string | null
  fecha_vencimiento: string | null
  moneda: string
  total: number
  ruc: string | null
  created_at: string
  dominios?: HostingDominio[]
}

/** Days until expiry, or null if no date */
function daysUntil(date: string | null): number | null {
  if (!date) return null
  const diff = new Date(date).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function expiryColor(days: number | null): string {
  if (days === null) return 'var(--text-secondary)'
  if (days > 30) return '#00b894'
  if (days > 7) return '#fdcb6e'
  return '#d63031'
}

const estadoConfig: Record<string, { label: string; badge: string; borderColor: string }> = {
  pagado: { label: 'En proceso', badge: 'badge-warning', borderColor: 'color-mix(in srgb, var(--warning) 40%, transparent)' },
  activo: { label: 'Activo', badge: 'badge-success', borderColor: 'color-mix(in srgb, var(--success) 40%, transparent)' },
  suspendido: { label: 'Suspendido', badge: 'badge-danger', borderColor: 'color-mix(in srgb, var(--danger) 40%, transparent)' },
  expirado: { label: 'Expirado', badge: 'badge-danger', borderColor: 'color-mix(in srgb, var(--text-muted) 40%, transparent)' },
}

export default function PortalHosting() {
  const { activeClienteId: clienteId } = usePortalCliente()
  const api = useApi()

  const [facturaLoading, setFacturaLoading] = useState<number | null>(null)
  const [facturaSolicitada, setFacturaSolicitada] = useState<Set<number>>(new Set())
  const [abriendoCpanel, setAbriendoCpanel] = useState<number | null>(null)
  const [verificandoDns, setVerificandoDns] = useState<number | null>(null)
  // "Agregar dominio" a una cuenta que hoy vive en subdominio temporal.
  const [addingFor, setAddingFor] = useState<number | null>(null)
  const [nuevoDominio, setNuevoDominio] = useState('')
  const [agregandoDominio, setAgregandoDominio] = useState(false)

  async function agregarDominio(pedidoId: number) {
    const dom = nuevoDominio.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '')
    if (!/^([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/.test(dom)) {
      showToast('Ingresa un dominio válido, p. ej. midominio.com', 'error'); return
    }
    setAgregandoDominio(true)
    try {
      const r = await api.post<{ ok: boolean; dominio: string }>(`/api/hosting/pedidos/${pedidoId}/agregar-dominio`, { dominio: dom })
      const finalDom = r?.dominio || dom
      // Refleja el cambio: dominio real + fila 'existente' pendiente de DNS → aparece el aviso.
      setPlans(prev => prev.map(p => p.id === pedidoId ? {
        ...p,
        dominio: finalDom,
        dominios: [{ id: -1, pedido_id: pedidoId, dominio: finalDom, tipo: 'existente', estado: 'configurado', dns_configurado: false }],
      } : p))
      setAddingFor(null); setNuevoDominio('')
      showToast('¡Dominio asignado! Ahora configura tu DNS para activarlo.', 'success')
    } catch (err: any) {
      showToast(err.message || 'No se pudo asignar el dominio', 'error')
    } finally {
      setAgregandoDominio(false)
    }
  }

  // Verifica si el dominio ya apunta al servidor; si sí, oculta el aviso "DNS pendiente".
  async function verificarDns(pedidoId: number) {
    setVerificandoDns(pedidoId)
    try {
      const r = await api.post<{ apunta: boolean; dominio: string }>(`/api/hosting/pedidos/${pedidoId}/check-dns`, {})
      if (r?.apunta) {
        showToast('¡Tu dominio ya apunta a nuestro servidor!', 'success')
        setPlans(prev => prev.map(p => p.id === pedidoId
          ? { ...p, dominios: p.dominios?.map(d => d.tipo === 'existente' ? { ...d, dns_configurado: true } : d) }
          : p))
      } else {
        showToast('Aún no detectamos el cambio. La propagación puede tardar hasta 24 h.', 'info')
      }
    } catch {
      showToast('No se pudo verificar. Intenta de nuevo en un momento.', 'error')
    } finally {
      setVerificandoDns(null)
    }
  }

  // Entra a cPanel sin pedir credenciales: el backend pide a WHM una URL de un solo uso.
  async function abrirCpanel(pedidoId: number) {
    // La pestaña se abre AHORA, sincronicamente. Si se abriera despues del await, el
    // navegador lo trataria como popup no solicitado y lo bloquearia.
    const ventana = window.open('', '_blank')
    setAbriendoCpanel(pedidoId)
    try {
      const r = await api.post<{ url: string }>(`/api/hosting/pedidos/${pedidoId}/cpanel-sso`, {})
      if (!r?.url) throw new Error('sin url')
      if (ventana) ventana.location.href = r.url
      else window.location.href = r.url // por si el navegador bloqueo la pestaña
    } catch {
      ventana?.close()
      showToast('No se pudo abrir cPanel. Intentalo de nuevo o escribenos por soporte.', 'error')
    } finally {
      setAbriendoCpanel(null)
    }
  }

  // Sigue la empresa seleccionada en el portal (va en la clave del cache); el
  // backend valida que el contacto este afiliado a ese cliente_id.
  const { data: plans = [], isLoading: loading, actualizar: setPlans } = useCachedData(
    ['portal-hosting', clienteId],
    () => api.get<HostingPlan[]>(`/api/hosting/mi-hosting?cliente_id=${clienteId}`).then(d => d ?? []),
    { activo: !!clienteId },
  )

  const handleSolicitarFactura = async (pedidoId: number) => {
    setFacturaLoading(pedidoId)
    try {
      await api.post(`/api/hosting/pedidos/${pedidoId}/solicitar-factura`, {})
      setFacturaSolicitada(prev => new Set(prev).add(pedidoId))
      showToast('Solicitud de factura enviada', 'success')
    } catch {
      showToast('Error al solicitar factura', 'error')
    } finally {
      setFacturaLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="portal-page-enter" style={{ padding: '2rem' }}>
        <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Hosting' }]} />
        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: '3rem' }}>
          <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
          Cargando...
        </div>
      </div>
    )
  }

  if (plans.length === 0) {
    return (
      <div className="portal-page-enter" style={{ padding: '2rem' }}>
        <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Hosting' }]} />
        <div style={{
          background: 'var(--card-bg, var(--bg-card))',
          border: '1px solid var(--border-color, #2a2a4a)',
          borderRadius: 12,
          padding: '3rem 2rem',
          textAlign: 'center',
          maxWidth: 480,
          margin: '2rem auto',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
            <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
            <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
            <line x1="6" y1="6" x2="6.01" y2="6" />
            <line x1="6" y1="18" x2="6.01" y2="18" />
          </svg>
          <h3 style={{ marginBottom: 8, color: 'var(--text-primary)' }}>Aún no tienes un plan de hosting</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
            Explora nuestros planes de hosting web con soporte 24/7, SSL gratuito y panel cPanel.
          </p>
          <a
            href="https://www.1conecta.com/infraestructura/hosting"
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary"
          >
            Ver planes de hosting
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="portal-page-enter" style={{ padding: '2rem' }}>
      <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Hosting' }]} />

      <div style={{ display: 'grid', gap: '16px' }}>
        {plans.map(plan => {
          const days = daysUntil(plan.fecha_vencimiento)
          const color = expiryColor(days)
          const cfg = estadoConfig[plan.estado] || estadoConfig.activo
          const hasDnsPending = plan.dominios?.some(d => d.tipo === 'existente' && !d.dns_configurado)
          const canRequestFactura = plan.ruc && ['activo', 'pagado'].includes(plan.estado) && !facturaSolicitada.has(plan.id)

          return (
            <div
              key={plan.id}
              style={{
                background: 'var(--card-bg, var(--bg-card))',
                border: `1px solid ${cfg.borderColor}`,
                borderRadius: 12,
                padding: '1.5rem',
                borderLeft: `3px solid ${cfg.borderColor}`,
              }}
            >
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                      <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                      <line x1="6" y1="6" x2="6.01" y2="6" />
                      <line x1="6" y1="18" x2="6.01" y2="18" />
                    </svg>
                    <strong style={{ fontSize: '1.1rem' }}>{plan.plan_nombre}</strong>
                  </div>
                  {(() => {
                    const esTemporal = !!plan.dominio && plan.dominio.endsWith('.1cta.site')
                    if (plan.dominio && !esTemporal) {
                      return <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{plan.dominio}</span>
                    }
                    if (esTemporal && plan.estado === 'activo') {
                      return (
                        <div>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Sin dominio propio</span>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => { setAddingFor(addingFor === plan.id ? null : plan.id); setNuevoDominio('') }}
                            style={{ marginLeft: 10, padding: '2px 10px', fontSize: '0.78rem' }}
                          >
                            {addingFor === plan.id ? 'Cancelar' : '+ Agregar dominio'}
                          </button>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #5C6078)', marginTop: 3 }}>
                            Dirección temporal: <span style={{ fontFamily: 'monospace' }}>{plan.dominio}</span>
                          </div>
                        </div>
                      )
                    }
                    return null
                  })()}
                </div>
                <span className={`badge ${cfg.badge}`}>
                  {cfg.label}
                </span>
              </div>

              {/* Formulario inline "Agregar dominio" (cuentas en subdominio temporal) */}
              {addingFor === plan.id && (
                <div style={{ background: 'color-mix(in srgb, var(--primary-light) 6%, transparent)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8 }}>Agregar un dominio propio</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      className="form-input"
                      placeholder="midominio.com"
                      value={nuevoDominio}
                      onChange={e => setNuevoDominio(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && agregarDominio(plan.id)}
                      style={{ flex: 1, minWidth: 200 }}
                    />
                    <button className="btn btn-primary" onClick={() => agregarDominio(plan.id)} disabled={agregandoDominio}>
                      {agregandoDominio ? 'Agregando…' : 'Agregar'}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
                    Úsalo si <strong>ya tienes un dominio</strong>: lo asignamos a tu hosting y luego apuntas su DNS a nuestra IP (te guiamos aquí mismo). ¿No tienes uno?{' '}
                    <a href="https://wa.me/51986252983?text=Hola%2C%20quiero%20registrar%20un%20dominio%20para%20mi%20hosting" target="_blank" rel="noreferrer" style={{ color: 'var(--accent-primary)' }}>Escríbenos para registrarlo</a>.
                  </div>
                </div>
              )}

              {/* State-specific content */}
              {plan.estado === 'pagado' && (
                <div style={{
                  background: 'color-mix(in srgb, var(--warning) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--warning) 20%, transparent)',
                  borderRadius: 10,
                  padding: '16px',
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}>
                  <div className="auth-spinner" style={{ width: 20, height: 20, borderWidth: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>Tu compra está en proceso</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', lineHeight: 1.5 }}>
                      Tu pago fue recibido y estamos preparando tu cuenta. La activación puede tardar de 1 a 24 horas. Recibirás un email con tus credenciales cuando tu hosting esté listo.
                    </div>
                  </div>
                </div>
              )}

              {plan.estado === 'suspendido' && (
                <div style={{
                  background: 'color-mix(in srgb, var(--danger) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)',
                  borderRadius: 10,
                  padding: '16px',
                  marginBottom: 16,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.9rem', color: 'var(--danger)' }}>
                    Cuenta suspendida por falta de pago
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', lineHeight: 1.5, marginBottom: 12 }}>
                    Tu sitio web no está disponible. Renueva tu plan para reactivar tu hosting.
                  </div>
                  <a
                    href="https://wa.me/51986252983?text=Hola%2C%20quiero%20renovar%20mi%20hosting%20suspendido"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    Contactar para renovar
                  </a>
                </div>
              )}

              {/* Info grid — only show for active/provisioned */}
              {plan.estado !== 'pagado' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>Periodo</small>
                    <div style={{ textTransform: 'capitalize' }}>{plan.periodo}</div>
                  </div>
                  {plan.fecha_vencimiento && (
                    <div>
                      <small style={{ color: 'var(--text-secondary)' }}>Vencimiento</small>
                      <div style={{ color, fontWeight: 600 }}>
                        {formatDate(plan.fecha_vencimiento)}
                        {days !== null && (
                          <span style={{ fontWeight: 400, fontSize: '0.8rem', marginLeft: 6 }}>
                            ({days > 0 ? `${days} días` : 'Vencido'})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {plan.cpanel_usuario && (
                    <div>
                      <small style={{ color: 'var(--text-secondary)' }}>Usuario cPanel</small>
                      <div style={{ fontFamily: 'monospace' }}>{plan.cpanel_usuario}</div>
                    </div>
                  )}
                  {plan.ip_servidor && (
                    <div>
                      <small style={{ color: 'var(--text-secondary)' }}>IP Servidor</small>
                      <div style={{ fontFamily: 'monospace' }}>{plan.ip_servidor}</div>
                    </div>
                  )}
                </div>
              )}

              {/* DNS Instructions — for existing domains that haven't been configured */}
              {hasDnsPending && plan.dominio && plan.estado === 'activo' && (
                <div style={{ marginBottom: 16 }}>
                  <DnsInstructions dominio={plan.dominio} ipServidor={plan.ip_servidor || undefined} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => verificarDns(plan.id)}
                      disabled={verificandoDns === plan.id}
                    >
                      {verificandoDns === plan.id ? 'Verificando…' : 'Ya configuré mi DNS — Verificar'}
                    </button>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      La propagación puede tardar de 1 a 24 h. Mientras tanto ya puedes trabajar en tu hosting.
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {plan.cpanel_url && plan.estado === 'activo' && (
                  <button
                    onClick={() => abrirCpanel(plan.id)}
                    disabled={abriendoCpanel === plan.id}
                    className="btn btn-sm btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    {abriendoCpanel === plan.id ? 'Abriendo…' : 'Abrir cPanel'}
                  </button>
                )}

                {canRequestFactura && (
                  <button
                    onClick={() => handleSolicitarFactura(plan.id)}
                    disabled={facturaLoading === plan.id}
                    className="btn btn-sm btn-ghost"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    {facturaLoading === plan.id ? 'Solicitando...' : 'Solicitar Factura'}
                  </button>
                )}

                {facturaSolicitada.has(plan.id) && (
                  <span className="badge badge-info" style={{ alignSelf: 'center' }}>
                    Factura solicitada
                  </span>
                )}

                <a
                  href="https://wa.me/51986252983?text=Hola%2C%20necesito%20soporte%20con%20mi%20hosting"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm btn-ghost"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
                  </svg>
                  Soporte
                </a>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
